import React, { useCallback, useEffect, useRef, useState } from 'react';
import Sidebar from '../Components/Siderbar';
import Footer from '../Components/Footer';
import { getChatMessages, getChats, replyToChat } from '../api/chat.api';
import { toast } from 'react-toastify';
import '../Customize_Css/Chat.css';

const Chat = () => {
  const [chats, setChats] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const threadRef = useRef(null);
  const selectedRef = useRef('');

  const scrollToEnd = () => {
    const node = threadRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  };

  const loadChats = useCallback(async () => {
    try {
      const data = await getChats();
      setChats(data);
    } catch (error) {
      toast.error(error.message || 'Could not load chats');
    } finally {
      setLoadingList(false);
    }
  }, []);

  const loadMessages = useCallback(async (userId, silent = false) => {
    if (!userId) return;
    try {
      const data = await getChatMessages(userId);
      if (selectedRef.current !== userId) return;
      if (data.chat && data.chat.userId && data.chat.userId !== userId) return;
      setMessages(data.messages || []);
      if (!silent) {
        setTimeout(scrollToEnd, 0);
      }
    } catch (error) {
      if (!silent) toast.error(error.message || 'Could not load messages');
    }
  }, []);

  useEffect(() => {
    loadChats();
    const timer = setInterval(loadChats, 4000);
    return () => clearInterval(timer);
  }, [loadChats]);

  useEffect(() => {
    selectedRef.current = selectedUserId;
    setMessages([]);
    setDraft('');
    if (!selectedUserId) return undefined;
    loadMessages(selectedUserId);
    const timer = setInterval(() => loadMessages(selectedUserId, true), 4000);
    return () => clearInterval(timer);
  }, [selectedUserId, loadMessages]);

  useEffect(() => {
    scrollToEnd();
  }, [messages]);

  const selected = chats.find((chat) => chat.userId === selectedUserId);

  const handleReply = async () => {
    const text = draft.trim();
    if (!text || !selectedUserId || sending) return;
    setSending(true);
    try {
      const data = await replyToChat(selectedUserId, text);
      if (!data.chat || data.chat.userId !== selectedUserId) return;
      setMessages(data.messages || []);
      setDraft('');
      await loadChats();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Could not send reply');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Sidebar />
      <div style={{ height: '92vh', overflow: 'hidden', width: '100%', padding: '15px' }}>
        <h4 className="mb-3">Chat</h4>
        <div className="admin-chat">
          <div className="admin-chat-list">
            <div className="admin-chat-list-head">
              <strong>Customers</strong>
            </div>
            <div className="admin-chat-list-body">
              {loadingList && <div className="p-3 text-muted">Loading chats...</div>}
              {!loadingList && chats.length === 0 && (
                <div className="p-3 text-muted">No customer messages yet.</div>
              )}
              {chats.map((chat) => (
                <button
                  key={chat.userId}
                  type="button"
                  className={`admin-chat-item ${chat.userId === selectedUserId ? 'active' : ''}`}
                  onClick={() => setSelectedUserId(chat.userId)}
                >
                  <div className="admin-chat-item-top">
                    <span className="admin-chat-name">{chat.customerName || 'Customer'}</span>
                    {chat.unreadForAdmin > 0 && (
                      <span className="admin-chat-badge">{chat.unreadForAdmin}</span>
                    )}
                  </div>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    {chat.phoneNo || chat.userId}
                  </div>
                  <div className="admin-chat-preview">
                    {chat.lastSender === 'Admin' ? 'Admin: ' : ''}
                    {chat.lastMessage}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="admin-chat-thread">
            {!selectedUserId && (
              <div className="admin-chat-empty">
                Select one customer. Your reply is sent only to that customer.
              </div>
            )}
            {selectedUserId && (
              <>
                <div className="admin-chat-thread-head">
                  <strong>{selected?.customerName || 'Customer'}</strong>
                  <div className="text-muted" style={{ fontSize: 13 }}>
                    {selected?.phoneNo || selectedUserId}
                  </div>
                </div>
                <div className="admin-chat-messages" ref={threadRef}>
                  {messages.length === 0 && (
                    <div className="admin-chat-empty">No messages in this chat yet.</div>
                  )}
                  {messages.map((message) => {
                    const isAdmin = message.sender === 'Admin';
                    return (
                      <div
                        key={message.id}
                        className={`admin-chat-bubble ${isAdmin ? 'admin' : 'customer'}`}
                      >
                        <div className="admin-chat-sender">
                          {isAdmin ? 'Admin' : selected?.customerName || 'Customer'}
                        </div>
                        <div>{message.text}</div>
                      </div>
                    );
                  })}
                </div>
                <div className="admin-chat-composer">
                  <input
                    className="form-control"
                    placeholder="Reply as Admin..."
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') handleReply();
                    }}
                  />
                  <button
                    type="button"
                    className="btn"
                    style={{ backgroundColor: '#257230', color: '#fff' }}
                    disabled={sending || !draft.trim()}
                    onClick={handleReply}
                  >
                    {sending ? 'Sending' : 'Send'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
};

export default Chat;
